import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { quotationsAPI } from '@/lib/api-client'
import { usePermission } from '@/hooks/usePermission'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { QuotationFormDialog } from './quotations/QuotationFormDialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Plus,
  Search,
  Scale,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
  Loader2,
  FolderOpen,
  Users,
  Package,
  MoreVertical,
  Pencil,
  Trash2,
  LayoutGrid,
  List,
} from 'lucide-react'

const STATUS_LABELS: Record<string, string> = {
  DRAFT: 'Rascunho',
  OPEN: 'Aberto',
  APPROVED: 'Aprovado',
  CANCELLED: 'Cancelado',
}

const STATUS_COLORS: Record<string, string> = {
  DRAFT: 'bg-neutral-100 text-neutral-600',
  OPEN: 'bg-blue-100 text-blue-700',
  APPROVED: 'bg-green-100 text-green-700',
  CANCELLED: 'bg-red-100 text-red-600',
}

export function Quotations() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const canCreate = usePermission('quotations:create')
  const canDelete = usePermission('quotations:delete')

  const [viewMode, setViewMode] = useState<'card' | 'table'>(() => {
    try {
      const stored = localStorage.getItem('viewMode:quotations')
      return stored === 'table' ? 'table' : 'card'
    } catch {
      return 'card'
    }
  })
  const [showCreateDialog, setShowCreateDialog] = useState(false)
  const [editingQC, setEditingQC] = useState<any>(null)
  const [deletingQC, setDeletingQC] = useState<any>(null)
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [sortKey, setSortKey] = useState<string | null>(null)
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc')

  const limit = viewMode === 'table' ? 15 : 9

  const { data, isLoading } = useQuery({
    queryKey: ['quotations', { page, search, status: statusFilter, limit }],
    queryFn: () => quotationsAPI.list({ page, limit, search: search || undefined, status: statusFilter || undefined }),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => quotationsAPI.delete(id),
    onSuccess: () => {
      toast.success('QC excluído.')
      queryClient.invalidateQueries({ queryKey: ['quotations'] })
      setDeletingQC(null)
    },
    onError: (e: Error) => toast.error(e.message),
  })

  const rawQuotations = data?.data || []
  const pagination = data?.pagination || { page: 1, totalPages: 1, total: 0 }

  const quotations = sortKey
    ? [...rawQuotations].sort((a: any, b: any) => {
        let av: any, bv: any
        if (sortKey === 'title') { av = a.title?.toLowerCase(); bv = b.title?.toLowerCase() }
        else if (sortKey === 'project') { av = a.project?.name?.toLowerCase() ?? ''; bv = b.project?.name?.toLowerCase() ?? '' }
        else if (sortKey === 'status') { av = a.status; bv = b.status }
        else if (sortKey === 'items') { av = a._count?.items ?? 0; bv = b._count?.items ?? 0 }
        else if (sortKey === 'suppliers') { av = a._count?.suppliers ?? 0; bv = b._count?.suppliers ?? 0 }
        if (av < bv) return sortDir === 'asc' ? -1 : 1
        if (av > bv) return sortDir === 'asc' ? 1 : -1
        return 0
      })
    : rawQuotations

  const handleSort = (key: string) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    else { setSortKey(key); setSortDir('desc') }
  }

  const SortIcon = ({ col }: { col: string }) => {
    if (sortKey !== col) return <ChevronsUpDown className="ml-1 h-3.5 w-3.5 text-neutral-400 inline" />
    return sortDir === 'asc'
      ? <ChevronUp className="ml-1 h-3.5 w-3.5 inline" />
      : <ChevronDown className="ml-1 h-3.5 w-3.5 inline" />
  }

  const handleViewMode = (v: 'card' | 'table') => {
    try {
      localStorage.setItem('viewMode:quotations', v)
    } catch {
      // ignore — localStorage may be blocked
    }
    setViewMode(v)
    setPage(1)
  }

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    setSearch(searchInput)
    setPage(1)
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900">Quadro de Concorrência</h1>
          <p className="mt-1 text-neutral-600">Compare orçamentos entre fornecedores e gere pedidos de compra</p>
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          <div className="flex items-center rounded-lg border p-0.5">
            <Button variant={viewMode === 'card' ? 'default' : 'ghost'} size="sm" className="h-7 text-xs" onClick={() => handleViewMode('card')}>
              <LayoutGrid className="mr-1 h-3.5 w-3.5" />Cards
            </Button>
            <Button variant={viewMode === 'table' ? 'default' : 'ghost'} size="sm" className="h-7 text-xs" onClick={() => handleViewMode('table')}>
              <List className="mr-1 h-3.5 w-3.5" />Tabela
            </Button>
          </div>
          {canCreate && (
            <Button onClick={() => setShowCreateDialog(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Novo QC
            </Button>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-4 sm:flex-row">
        <form onSubmit={handleSearch} className="flex flex-1 gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
            <Input
              placeholder="Buscar por título..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="pl-10"
            />
          </div>
          <Button type="submit" variant="secondary">Buscar</Button>
        </form>
        <Select value={statusFilter || 'ALL'} onValueChange={(v) => { setStatusFilter(v === 'ALL' ? '' : v); setPage(1) }}>
          <SelectTrigger className="w-full sm:w-[180px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">Todos os status</SelectItem>
            {Object.entries(STATUS_LABELS).map(([value, label]) => (
              <SelectItem key={value} value={value}>{label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-24">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : quotations.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-lg border bg-neutral-50 p-16">
          <Scale className="h-16 w-16 text-neutral-300" />
          <h3 className="mt-4 text-xl font-medium text-neutral-900">
            {search || statusFilter ? 'Nenhum QC encontrado' : 'Nenhum Quadro de Concorrência'}
          </h3>
          <p className="mt-2 text-neutral-500">
            {search || statusFilter ? 'Tente ajustar os filtros.' : 'Crie um QC para comparar preços entre fornecedores.'}
          </p>
          {canCreate && !search && !statusFilter && (
            <Button className="mt-6" onClick={() => setShowCreateDialog(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Criar Quadro de Concorrência
            </Button>
          )}
        </div>
      ) : viewMode === 'card' ? (
        <>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {quotations.map((qc: any) => (
              <Card
                key={qc.id}
                className="cursor-pointer transition-shadow hover:shadow-md"
                onClick={() => navigate(`/quotations/${qc.id}`)}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <Scale className="h-5 w-5 shrink-0 text-primary" />
                      <CardTitle className="text-base line-clamp-1">{qc.title}</CardTitle>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Badge variant="outline" className={`text-xs ${STATUS_COLORS[qc.status] || ''}`}>
                        {STATUS_LABELS[qc.status] || qc.status}
                      </Badge>
                      {(canCreate || canDelete) && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                            <Button variant="ghost" size="icon" className="h-7 w-7">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                            {canCreate && (
                              <DropdownMenuItem onClick={() => setEditingQC(qc)}>
                                <Pencil className="mr-2 h-4 w-4" />Editar
                              </DropdownMenuItem>
                            )}
                            {canDelete && (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem className="text-destructive" onClick={() => setDeletingQC(qc)}>
                                  <Trash2 className="mr-2 h-4 w-4" />Excluir
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  {qc.project && (
                    <div className="flex items-center gap-2 text-sm text-neutral-600">
                      <FolderOpen className="h-3.5 w-3.5 text-neutral-400 shrink-0" />
                      <span className="truncate">{qc.project.name}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-4 text-sm text-neutral-500">
                    <div className="flex items-center gap-1">
                      <Package className="h-3.5 w-3.5" />
                      <span>{qc._count?.items ?? 0} materiais</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Users className="h-3.5 w-3.5" />
                      <span>{qc._count?.suppliers ?? 0} fornecedores</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {pagination.totalPages > 1 && (
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm text-neutral-500">Mostrando {quotations.length} de {pagination.total}</p>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                  <ChevronLeft className="h-4 w-4" />Anterior
                </Button>
                <span className="text-sm text-neutral-600">{page} / {pagination.totalPages}</span>
                <Button variant="outline" size="sm" disabled={page >= pagination.totalPages} onClick={() => setPage(page + 1)}>
                  Próximo<ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </>
      ) : (
        <>
          <div className="rounded-lg border bg-white overflow-x-auto">
            <div className="min-w-[560px]">
            <Table>
              <TableHeader>
                <TableRow className="bg-neutral-50">
                  {[
                    { key: 'title', label: 'Título', className: 'font-semibold cursor-pointer select-none' },
                    { key: 'project', label: 'Projeto', className: 'font-semibold cursor-pointer select-none' },
                    { key: 'status', label: 'Status', className: 'font-semibold cursor-pointer select-none text-center' },
                    { key: 'items', label: 'Materiais', className: 'font-semibold cursor-pointer select-none text-center' },
                    { key: 'suppliers', label: 'Fornecedores', className: 'font-semibold cursor-pointer select-none text-center' },
                  ].map(({ key, label, className }) => (
                    <TableHead key={key} className={className} onClick={() => handleSort(key)}>
                      {label}<SortIcon col={key} />
                    </TableHead>
                  ))}
                  <TableHead className="font-semibold w-10" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {quotations.map((qc: any) => (
                  <TableRow
                    key={qc.id}
                    className="cursor-pointer hover:bg-neutral-50"
                    onClick={() => navigate(`/quotations/${qc.id}`)}
                  >
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        <Scale className="h-4 w-4 text-primary shrink-0" />
                        {qc.title}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-neutral-500">
                      {qc.project ? (
                        <div className="flex items-center gap-1">
                          <FolderOpen className="h-3.5 w-3.5 shrink-0" />
                          {qc.project.name}
                        </div>
                      ) : '—'}
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge variant="outline" className={`text-xs ${STATUS_COLORS[qc.status] || ''}`}>
                        {STATUS_LABELS[qc.status] || qc.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-center text-sm text-neutral-600">
                      {qc._count?.items ?? 0}
                    </TableCell>
                    <TableCell className="text-center text-sm text-neutral-600">
                      {qc._count?.suppliers ?? 0}
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      {(canCreate || canDelete) && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-7 w-7">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {canCreate && (
                              <DropdownMenuItem onClick={() => setEditingQC(qc)}>
                                <Pencil className="mr-2 h-4 w-4" />Editar
                              </DropdownMenuItem>
                            )}
                            {canDelete && (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem className="text-destructive" onClick={() => setDeletingQC(qc)}>
                                  <Trash2 className="mr-2 h-4 w-4" />Excluir
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </div>
          </div>

          {pagination.totalPages > 1 && (
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm text-neutral-500">Mostrando {quotations.length} de {pagination.total}</p>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                  <ChevronLeft className="h-4 w-4" />Anterior
                </Button>
                <span className="text-sm text-neutral-600">{page} / {pagination.totalPages}</span>
                <Button variant="outline" size="sm" disabled={page >= pagination.totalPages} onClick={() => setPage(page + 1)}>
                  Próximo<ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      <QuotationFormDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
        onCreated={(qc) => navigate(`/quotations/${qc.id}`)}
      />
      <QuotationFormDialog
        open={!!editingQC}
        onOpenChange={(open: boolean) => !open && setEditingQC(null)}
        quotation={editingQC}
      />

      <Dialog open={!!deletingQC} onOpenChange={(open: boolean) => !open && setDeletingQC(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Excluir Quadro de Concorrência</DialogTitle>
            <DialogDescription>
              Tem certeza que deseja excluir "{deletingQC?.title}"? Esta ação não pode ser desfeita.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeletingQC(null)}>Cancelar</Button>
            <Button variant="destructive" disabled={deleteMutation.isPending}
              onClick={() => deletingQC && deleteMutation.mutate(deletingQC.id)}>
              {deleteMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Excluir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
